<template>
    <div
      class="fixed top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-50"
      v-if="showing"
    >
      <div
        class="bg-gray-900 text-green-500 p-3 md:p-5 border-2 border-green-500 rounded-lg w-11/12 max-w-3xl max-h-[90%] overflow-y-auto font-mono relative"
      >
        <!-- Error message display -->
        <div v-if="errorMessage" class="bg-red-500 text-white p-2">
          {{ errorMessage }}
        </div>

          <div class="flex flex-col items-center mb-4">
            <h2 class="text-xl md:text-2xl font-bold tracking-wider">Bus Timetable</h2>
            <div class="flex justify-between w-full text-base md:text-lg mt-2">
              <span>Station: {{ station.name }}</span>
              <span>Time: {{ formattedTime }}</span>
              <button
                v-if="!selectedBus"
                @click="close"
                class="bg-gray-700 text-white py-1 px-3 rounded hover:bg-gray-600"
              >
                Close
              </button>
              <button
                v-else
                @click="back"
                class="bg-gray-700 text-white py-1 px-3 rounded hover:bg-gray-600"
              >
                Back
              </button>
            </div>
          </div>

          <!-- Search input -->
          <div v-if="!selectedBus" class="relative">
            <input
              type="text"
              v-model="searchText"
              placeholder="Search for a destination..."
              class="w-full p-2 pr-10 mb-4 bg-gray-800 border border-green-500 rounded text-white"
            />
            <button
              v-if="searchText"
              @click="clearSearch"
              class="absolute right-2 top-2 text-gray-400 hover:text-white focus:outline-none"
              type="button"
            >
              ✕
            </button>
          </div>
    
          <div v-if="loading" class="text-center text-2xl">
            Loading timetable...
          </div>

          <div
            v-else-if="!selectedBus && buses.length === 0"
            class="text-center text-xl text-yellow-400 p-4"
          >
            No buses available for this destination or time.
          </div>
    
          <!-- Main timetable view (list of buses) -->
          <div v-else-if="!selectedBus">
            <table class="w-full border-collapse">
              <thead>
                <tr>
                  <th class="hidden md:table-cell border border-green-500 p-2 text-center"></th>
                  <th class="border border-green-500 p-2 text-center">Destination</th>
                  <th class="border border-green-500 p-2 text-center">Depart</th>
                  <th class="border border-green-500 p-2 text-center">Arrive</th>
                  <th class="border border-green-500 p-2 text-center">Carrier</th>
                  <th class="border border-green-500 p-2 text-center"></th>
                </tr>
              </thead>
              <tbody>
                <!-- Current day buses -->
                <tr
                  v-for="(bus, index) in currentDayBuses"
                  :key="'current-' + index"
                  @click="selectBus(bus)"
                  class="cursor-pointer hover:bg-gray-700"
                >
                  <td class="hidden md:table-cell border border-green-500 text-center">
                    <img :src="bus.carrier_image" alt="Carrier" class="h-7 mx-auto" />
                  </td>
                  <td class="border border-green-500 p-2 text-center">
                    {{ bus.to_station_name }}
                  </td>
                  <td class="border border-green-500 p-2 text-center">
                    {{ bus.depart }}
                  </td>
                  <td class="border border-green-500 p-2 text-center">
                    {{ bus.arrive }}
                  </td>
                  <td class="border border-green-500 p-2 text-center">
                    {{ bus.carrier_name }}
                  </td>
                  <td class="border border-green-500 p-2 text-center">
                    {{ bus.currency_symbol }}{{ bus.price?.toFixed(2) }}
                  </td>
                </tr>
                <!-- Tomorrow header and buses -->
                <template v-if="nextDayBuses.length > 0">
                  <tr>
                    <td :colspan="6" class="border border-green-500 p-3 text-center bg-green-900 font-bold text-lg">
                      Tomorrow
                    </td>
                  </tr>
                  <tr
                    v-for="(bus, index) in nextDayBuses"
                    :key="'next-' + index"
                    @click="selectBus(bus)"
                    class="cursor-pointer hover:bg-gray-700"
                  >
                    <td class="hidden md:table-cell border border-green-500 text-center">
                      <img :src="bus.carrier_image" alt="Carrier" class="h-7 mx-auto" />
                    </td>
                    <td class="border border-green-500 p-2 text-center">
                      {{ bus.to_station_name }}
                    </td>
                    <td class="border border-green-500 p-2 text-center">
                      {{ bus.depart }}
                    </td>
                    <td class="border border-green-500 p-2 text-center">
                      {{ bus.arrive }}
                    </td>
                    <td class="border border-green-500 p-2 text-center">
                      {{ bus.carrier_name }}
                    </td>
                    <td class="border border-green-500 p-2 text-center">
                      {{ bus.currency_symbol }}{{ bus.price?.toFixed(2) }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
    
          <!-- Purchase view with timeline: Displayed when a bus is selected -->
          <div v-else class="bg-gray-800 p-5 border border-green-500 rounded mt-4 text-center">
            <h3 class="text-xl font-bold mb-3">Select Your destination</h3>
    
            <!-- Loading spinner for bus stops -->
            <div v-if="loadingStops" class="text-center mb-4 text-2xl">
              Loading stops...
            </div>
    
            <!-- Timeline view for bus stops -->
            <div v-else-if="busStops.length > 0" class="flex flex-col space-y-2 overflow-y-auto mb-4">
              <div
                v-for="(stop, index) in busStops"
                :key="index"
                @click="selectStop(stop)"
                :class="['cursor-pointer p-2 border rounded', selectedStop && selectedStop.station_id === stop.station_id ? 'bg-green-500 text-white' : 'bg-gray-700 text-white']"
              >
                <p><strong>{{ stop.station_name }}</strong></p>
                <p>Arrive: {{ stop.arrive }}</p>
                <p>Price: {{ stop.currency_symbol }}{{ stop.price?.toFixed(2) }}</p>
              </div>
            </div>
    
            <!-- Confirm ticket button -->
            <Button
              class="bg-gray-700 text-white py-2 px-4 rounded hover:bg-gray-600 mt-4"
              :class="{'bg-green-500 hover:bg-green-600': selectedStop}"
              :disabled="!selectedStop"
              :loading="confirming"
              @click="confirmTicket"
            >
              Confirm Ticket
            </Button>
          </div>
      </div>
    </div>
  </template>
    
<script setup>
import { validRoute } from '@/utils/gameplay.js';
import { timeStringToSeconds } from '@/utils/time.js';
import { ref, computed, watch } from 'vue';
import Button from '@/components/Button.vue';

const props = defineProps({
  station: {
    type: Object,
    required: false,
  },
  time: {
    type: Number,
    required: true,
  },
  dayOfWeek: {
    type: Number,
    required: true,
  },
});

const BaseUrl = ref(baseUrl);
const showing = defineModel('modelValue', { default: false, type: Boolean });
const emit = defineEmits(['bought-ticket', 'close', 'wait-for-bus']);

const errorMessage = ref(null);

const confirming = ref(false);

const buses = ref([]);
const loading = ref(false);
const error = ref(null);
const selectedBus = ref(null);

// New state for bus stops timeline
const busStops = ref([]);
const selectedStop = ref(null);
const loadingStops = ref(false);

const searchText = ref('');

const formattedTime = computed(() => {
  const hours = Math.floor(props.time / 3600) % 24;
  const minutes = Math.floor(props.time / 60) % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
});

// Separate buses into current day and next day
const currentDayBuses = computed(() => {
  return buses.value.filter(bus => {
    // If depart_day is not set, assume it's current day
    if (bus.depart_day === undefined || bus.depart_day === null) {
      return true;
    }
    // Compare as numbers to handle both string and number types
    return Number(bus.depart_day) === Number(props.dayOfWeek);
  });
});

const nextDayBuses = computed(() => {
  return buses.value.filter(bus => {
    // Only include buses that have depart_day set and it's different from current day
    if (bus.depart_day === undefined || bus.depart_day === null) {
      return false;
    }
    return Number(bus.depart_day) !== Number(props.dayOfWeek);
  });
});

function parseTime(hhmmss) {
  const parts = hhmmss.split(':').map(Number);
  const [h, m, s = 0] = parts;          // default s to 0 if missing
  return h * 3600 + m * 60 + s;
}

function isTimeAfter(timeA, timeB) {
  return parseTime(timeA) >= parseTime(timeB);
}

const stationName = computed(() => {
  if (buses.value.length > 0) {
    return buses.value[0].from_station_name;
  }
  return 'Unknown Station';
});

async function fetchBuses() {
  const timetableTime = props.time;
  const timetableDay = props.dayOfWeek;
  loading.value = true;
  try {
    let url = `${baseUrl}api/coaches?from_id=${props.station.id}&time=${formattedTime.value}&day=${props.dayOfWeek}`;
    if (searchText.value.trim()) {
      url += `&q=${encodeURIComponent(searchText.value.trim())}`;
    }
    const response = await fetch(url);
    const data = await response.json();
    if (!response.ok || !Array.isArray(data.data)) throw new Error('Timetable unavailable');
    buses.value = data.data.map(service => ({ ...service, departure_local: Math.floor(timetableTime / 86400) * 86400 + ((Number(service.depart_day) - Number(timetableDay) + 7) % 7) * 86400 + timeStringToSeconds(service.depart) }));
  } catch (err) {
    error.value = 'Error fetching bus timetable';
    console.error(err);
  } finally {
    loading.value = false;
  }
}

// When a bus row is clicked, select the bus and immediately fetch its stops.
async function selectBus(bus) {
  if(props.time >= bus.departure_local) {
    errorMessage.value = 'This bus has already departed.';
    setTimeout(() => {
      errorMessage.value = null;
    }, 5000);
    // If the bus has already departed, do not allow selection and refetch the list.
    await fetchBuses();
    return;
  }
  selectedBus.value = bus;
  // Immediately fetch bus stops timeline.
  await fetchBusStops(bus);
}

function cancelPurchase() {
  selectedBus.value = null;
  busStops.value = [];
  selectedStop.value = null;
}

function formatPrice(price) {
  return `$${price.toFixed(2)}`;
}

function close() {
  emit('close');
  selectedBus.value = null;
  buses.value = [];
  busStops.value = [];
  selectedStop.value = null;
}

function back() {
  selectedBus.value = null;
  busStops.value = [];
  selectedStop.value = null;
}

function exitStation() {
  close();
}

// New: Fetch bus stops for the selected bus using the API.
// The URL format is: api/coaches/$busId?from_id=$stationId
async function fetchBusStops(bus) {
  loadingStops.value = true;
  try {
    const url = `${baseUrl}api/coaches/${bus.id}?from_id=${props.station.id}&day=${bus.depart_day}&source=${bus.source || ''}`;
    const response = await fetch(url);
    const data = await response.json();
    // Assume the API returns an array of bus stop objects.
    if (!response.ok || !Array.isArray(data.data)) throw new Error('Stops unavailable');
    busStops.value = data.data.filter(stop => String(stop.station_id) !== String(props.station.id));
    selectedStop.value = busStops.value.at(-1) || null;
  } catch (err) {
    console.error("Error fetching bus stops:", err);
  } finally {
    loadingStops.value = false;
  }
}
  
function selectStop(stop) {
  selectedStop.value = stop;
}
  
async function confirmTicket() {
  if (confirming.value || !selectedBus.value || !selectedStop.value) return;
  confirming.value = true;

  if(props.time >= selectedBus.value.departure_local) {
    selectedBus.value = null;
    selectedStop.value = null;
    errorMessage.value = 'This bus has already departed.';
    setTimeout(() => {
      errorMessage.value = null;
    }, 5000);
    confirming.value = false;
    // If the bus has already departed, do not allow selection and refetch the list.
    await fetchBuses();
    return;
  }
  
  try {
    const url = `${baseUrl}api/coaches/${selectedBus.value.id}/route?from_id=${props.station.id}&to_id=${selectedStop.value.station_id}&source=${selectedBus.value.source || ''}`;
    const response = await fetch(url);
    const routeData = await response.json();
    if (!response.ok || !validRoute(routeData.data)) throw new Error('Route unavailable');
    if (props.time >= selectedBus.value.departure_local) throw new Error('Departure has passed');

    const num_days_delay = Math.floor(selectedBus.value.departure_local / 86400) - Math.floor(props.time / 86400);

    const transport = {
      ...selectedBus.value,
      arrive: selectedStop.value.arrive,
      duration_seconds: selectedStop.value.duration_seconds,
      duration_minutes: Math.ceil(selectedStop.value.duration_seconds / 60),
      price: selectedStop.value.price,
      to_id: selectedStop.value.station_id,
      to_lat: selectedStop.value.lat,
      to_lng: selectedStop.value.lng,
      to_station_name: selectedStop.value.station_name,
      depart_delay_days: num_days_delay,
    };

    // Remove any stops after transport.to_id from busStops
    const stop = busStops.value.find(s => s.station_id === selectedStop.value.station_id);
    let trimmedBusStops = busStops.value;
    if (stop) {
      const stopIndex = busStops.value.indexOf(stop);
      trimmedBusStops = busStops.value.slice(0, stopIndex + 1);
    }

    emit('bought-ticket', {station: props.station, transport, route: routeData.data, stops: trimmedBusStops});

    confirming.value = false;
    selectedBus.value = null;
    busStops.value = [];
    selectedStop.value = null;
  } catch (err) {
    console.error("Error fetching bus route:", err);
    errorMessage.value = 'Could not load this journey. Refresh the timetable and try again. No money was charged.';
  } finally {
    confirming.value = false;
  }
}

// Debounce functionality for search
let searchTimeout = null;

function debounceSearch() {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  searchTimeout = setTimeout(() => {
    if (!selectedBus.value) {
      fetchBuses();
    }
  }, 500); // 500ms delay
}

// Watch for search text changes
watch(searchText, () => {
  debounceSearch();
});

function clearSearch() {
  searchText.value = '';
}

watch(showing, (newVal) => {
  if (newVal) {
    // Reset state when showing the component
    selectedBus.value = null;
    buses.value = [];
    busStops.value = [];
    selectedStop.value = null;
    searchText.value = '';
    fetchBuses();
  }
});

</script>
  